import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import './linenDeposit.css';

const SET_PRICE = 100; // AED per linen set — the ledger stores amounts; sets are amount/100.

const money = (n) => {
    const num = Number(n || 0);
    return `AED ${num % 1 === 0 ? num : num.toFixed(2)}`;
};

const formatDate = (d) => {
    if (!d) return '—';
    const dt = DateTime.fromISO(d, { zone: 'utc' }).setZone('Asia/Dubai');
    return dt.isValid ? dt.toFormat('dd LLL yyyy') : String(d);
};

const todayISO = () => DateTime.now().setZone('Asia/Dubai').toFormat('yyyy-MM-dd');

/* An amount reads as N sets only when it IS N clean sets. Legacy rows carry
   odd figures (AED 234 etc.) — those stay AED-first everywhere. */
const asSets = (amount) => {
    const n = Number(amount || 0);
    return n > 0 && Math.abs(n / SET_PRICE - Math.round(n / SET_PRICE)) < 0.001
        ? Math.round(n / SET_PRICE) : null;
};
const setsLabel = (n) => `${n} set${n === 1 ? '' : 's'}`;

function LinenDepositSection({ propertyId, isLinenHire }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const isManager = (localStorage.getItem('userRole') || '').toLowerCase() === 'manager';

    // ---- Refund modal ----
    // mode 'wallet' (manager: money back into the wallet via the tax-free
    // credit-note chain) or 'manual' (recording a refund that already happened
    // outside the portal — the original flow, proof required).
    const [refundDeposit, setRefundDeposit] = useState(null);
    const [refundMode, setRefundMode] = useState('wallet');
    const [refundSets, setRefundSets] = useState(1);
    const [refundForm, setRefundForm] = useState({ amount: '', refunded_at: todayISO(), note: '', invoice_reference: '' });
    const [receiptFile, setReceiptFile] = useState(null);
    const [refundError, setRefundError] = useState('');
    // Over-cap refund (2026-09-23): the server refuses until a manager confirms
    // the ledger was checked — duplicate rows from deleted bookings must not
    // turn into a double refund by accident.
    const [refundOverCap, setRefundOverCap] = useState(false);
    const [refundOverCapAck, setRefundOverCapAck] = useState(false);
    const [refundSubmitting, setRefundSubmitting] = useState(false);

    // ---- Manual "record deposit" modal (sets-first since 2026-09-02) ----
    const [recordOpen, setRecordOpen] = useState(false);
    const [recordSets, setRecordSets] = useState(1);
    const [recordCustom, setRecordCustom] = useState(false);
    const [recordForm, setRecordForm] = useState({ amount: '', booking_date: todayISO(), note: '' });
    const [recordError, setRecordError] = useState('');
    const [recordSubmitting, setRecordSubmitting] = useState(false);
    // Over-cap consent — appears only after the backend has actually refused.
    const [overCapBlocked, setOverCapBlocked] = useState(false);
    const [allowOverCap, setAllowOverCap] = useState(false);

    const [clientInvoices, setClientInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    // The booking WE collected the linens on for the final return (owner,
    // 2026-09-03) — optional, recorded on the refund row.
    const [collectedBooking, setCollectedBooking] = useState(null);
    const [pastBookings, setPastBookings] = useState([]);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await axios.get(`/api/linen-deposits/property/${propertyId}`);
            setStatus(res.data);
        } catch (e) {
            console.error('Error fetching linen deposit status:', e);
            setStatus(null);
        } finally {
            setLoading(false);
        }
    }, [propertyId]);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    if (loading || !status) return null;
    if (!status.charged && !isLinenHire) return null;

    const submitRecord = async () => {
        const amount = recordCustom ? parseFloat(recordForm.amount) : recordSets * SET_PRICE;
        if (!Number.isFinite(amount) || amount <= 0) {
            setRecordError('Please enter a valid deposit amount.');
            return;
        }
        setRecordSubmitting(true);
        setRecordError('');
        try {
            await axios.post(`/api/linen-deposits/property/${propertyId}`, {
                amount,
                booking_date: recordForm.booking_date || undefined,
                note: (recordForm.note || '').trim() || undefined,
                ...(allowOverCap ? { allow_over_cap: true } : {}),
            });
            setRecordOpen(false);
            setOverCapBlocked(false);
            setAllowOverCap(false);
            await fetchStatus();
        } catch (e) {
            console.error('Error recording linen deposit:', e);
            const message = e.response?.data?.error || 'Failed to record deposit.';
            if (e.response?.status === 400 && /over-cap|would exceed it/i.test(message)) {
                setOverCapBlocked(true);
                setAllowOverCap(false);
            }
            setRecordError(message);
        } finally {
            setRecordSubmitting(false);
        }
    };

    const openRefundModal = (deposit) => {
        const remaining = Number(deposit.amount) - Number(deposit.refunded_total || 0);
        const remSets = asSets(remaining);
        setRefundMode(isManager ? 'wallet' : 'manual');
        setRefundSets(remSets || 1);
        setRefundForm({ amount: remaining.toFixed(2), refunded_at: todayISO(), note: '', invoice_reference: '' });
        setReceiptFile(null);
        setRefundError('');
        setRefundOverCap(false); setRefundOverCapAck(false);
        setRefundDeposit(deposit);
        setCollectedBooking(null);
        // Every booking of this property, today's and scheduled ones included —
        // the "past bookings" list this used to borrow stopped at five rows and
        // hid a pickup whose scheduled time had not come yet (owner, 2026-09-10).
        axios.get(`/api/linen-deposits/property/${propertyId}/collection-bookings`)
            .then((res) => setPastBookings(res.data?.bookings || []))
            .catch(() => setPastBookings([]));
        setInvoicesLoading(true);
        axios.get(`/api/invoices?clientId=${deposit.client_id}&limit=200`)
            .then((res) => {
                const rows = (res.data?.invoices || []).filter((inv) => inv.zoho_invoice_number);
                setClientInvoices(rows);
            })
            .catch(() => setClientInvoices([]))
            .finally(() => setInvoicesLoading(false));
    };

    const collectedOptions = pastBookings.map((b) => ({
        value: b.booking_id,
        label: `#${b.booking_id} · ${DateTime.fromISO(b.booking_start, { zone: 'Asia/Dubai' }).toFormat('d LLL yyyy, HH:mm')} · ${b.booking_type}`
            + (b.upcoming ? ' · scheduled' : ''),
    }));

    const invoiceOptions = clientInvoices.map((inv) => ({
        value: inv.zoho_invoice_number,
        label: `${inv.zoho_invoice_number} · ${formatDate(inv.created_at)} · AED ${Number(inv.total_amount || 0).toFixed(2)}`,
    }));

    const closeRefundModal = () => {
        if (refundSubmitting) return;
        setRefundDeposit(null);
        setRefundError('');
    };

    const submitRefund = async () => {
        const remaining = Number(refundDeposit.amount) - Number(refundDeposit.refunded_total || 0);
        const remSets = asSets(remaining);
        const amount = refundMode === 'wallet' && remSets
            ? refundSets * SET_PRICE
            : parseFloat(refundForm.amount);
        if (!Number.isFinite(amount) || amount <= 0) {
            setRefundError('Please enter a valid refund amount.');
            return;
        }

        if (!collectedBooking?.value) {
            setRefundError('Select the booking the linens were collected on for the final return.');
            return;
        }

        if (refundMode === 'wallet') {
            setRefundSubmitting(true);
            setRefundError('');
            try {
                const res = await axios.post(`/api/linen-deposits/${refundDeposit.id}/refunds`, {
                    mode: 'wallet',
                    amount,
                    note: (refundForm.note || '').trim() || undefined,
                    collected_booking_id: collectedBooking?.value || undefined,
                    ...(refundOverCap && refundOverCapAck ? { allow_over_cap: true } : {}),
                });
                const r = res.data?.refund || {};
                toast.success(
                    `Refunded ${money(amount)} to the wallet`
                    + (r.creditnote_number ? ` — credit note ${r.creditnote_number} (VAT-free)` : '')
                );
                setRefundDeposit(null);
                await fetchStatus();
            } catch (e) {
                console.error('Error refunding deposit to wallet:', e);
                setRefundError(e.response?.data?.error || 'Failed to refund to the wallet.');
                if (['DEPOSIT_LEDGER_OVER_CAP', 'DEPOSIT_REFUND_OVER_CAP'].includes(e.response?.data?.code)) setRefundOverCap(true);
            } finally {
                setRefundSubmitting(false);
            }
            return;
        }

        // Manual mode — recording a refund that already happened. Proof required.
        const invoiceRef = (refundForm.invoice_reference || '').trim();
        if (!invoiceRef && !receiptFile) {
            setRefundError('Attach proof of the refund: an invoice/credit-note number or a transfer receipt.');
            return;
        }
        setRefundSubmitting(true);
        setRefundError('');
        try {
            let receipt_key;
            let receipt_url;
            if (receiptFile) {
                const presign = await axios.post('/api/linen-deposits/refund-receipt-presign', {
                    fileName: receiptFile.name,
                    fileType: receiptFile.type || 'application/octet-stream',
                });
                await axios.put(presign.data.url, receiptFile, {
                    // Spaces CORS has no Allow-Credentials; the global axios
                    // withCredentials=true default would make the browser BLOCK
                    // this cross-origin PUT — must be off for presigned uploads.
                    withCredentials: false,
                    headers: { 'Content-Type': receiptFile.type || 'application/octet-stream', 'x-amz-acl': 'public-read' },
                });
                receipt_key = presign.data.fileKey;
                receipt_url = presign.data.fileUrl;
            }
            await axios.post(`/api/linen-deposits/${refundDeposit.id}/refunds`, {
                amount,
                refunded_at: refundForm.refunded_at || undefined,
                note: (refundForm.note || '').trim() || undefined,
                invoice_reference: invoiceRef || undefined,
                receipt_key,
                receipt_url,
                collected_booking_id: collectedBooking?.value || undefined,
            });
            setRefundDeposit(null);
            await fetchStatus();
        } catch (e) {
            console.error('Error recording linen deposit refund:', e);
            setRefundError(e.response?.data?.error || 'Failed to record refund.');
        } finally {
            setRefundSubmitting(false);
        }
    };

    const refundsFor = (depositId) =>
        (status.refunds || []).filter((r) => Number(r.deposit_id) === Number(depositId));

    const outstanding = Number(status.outstanding || 0);
    const totalCharged = (status.deposits || []).reduce((s, d) => s + Number(d.amount || 0), 0);
    const totalRefunded = (status.deposits || []).reduce((s, d) => s + Number(d.refunded_total || 0), 0);
    const chargedSets = asSets(totalCharged);
    const refundedSets = totalRefunded > 0.009 ? asSets(totalRefunded) : 0;
    const outstandingSets = asSets(outstanding);

    return (
        <div className="dp-card">
            <h2>Linen Deposit</h2>

            {(!status.client_registration
                || DateTime.fromISO(status.client_registration, { zone: 'Asia/Dubai' })
                    < DateTime.fromISO('2026-07-15', { zone: 'Asia/Dubai' })) && (
                <div className="ld-legacy-warning">
                    ⚠️ This client was registered before 15 July 2026 — please double-check the
                    deposit amount is correct (older records may not match the per-bed rule).
                </div>
            )}

            {status.charged && !status.has_all_inclusive_booking && (
                <div className="ld-legacy-warning">
                    ⚠️ This property hasn't had an All-Inclusive cleaning yet, but the linen deposit was
                    already charged{status.deposits[0]?.booking_id
                        ? <> at <a href={`/bookings/${status.deposits[0].booking_id}`} className="ld-link"><strong>booking #{status.deposits[0].booking_id}</strong></a></>
                        : ' (recorded manually)'} — it will not be charged again.
                </div>
            )}

            {!status.charged ? (
                <div className="ld-empty">
                    No linen deposit on record — a one-time deposit (AED 100 per set) is added automatically
                    to the first All-Inclusive booking.
                    <div style={{ marginTop: 8 }}>
                        <button
                            type="button"
                            className="ld-refund-btn"
                            onClick={() => {
                                setRecordSets(1);
                                setRecordCustom(false);
                                setRecordForm({ amount: '', booking_date: todayISO(), note: '' });
                                setRecordError('');
                                setOverCapBlocked(false);
                                setAllowOverCap(false);
                                setRecordOpen(true);
                            }}
                        >
                            Record deposit manually
                        </button>
                        <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>
                            (already paid by bank/cash — prevents the automatic charge)
                        </span>
                    </div>
                </div>
            ) : (
                <>
                    {/* Held / returned / outstanding at a glance, in SETS when the
                        amounts are clean multiples of 100, AED always. */}
                    <div className="ld-setChips">
                        <span className="ld-setChip">
                            <strong>{chargedSets != null ? setsLabel(chargedSets) : money(totalCharged)}</strong> charged
                        </span>
                        <span className={`ld-setChip ${totalRefunded > 0.009 ? 'ld-setChip--ok' : ''}`}>
                            <strong>{refundedSets != null && totalRefunded > 0.009 ? setsLabel(refundedSets) : money(totalRefunded)}</strong> refunded
                        </span>
                        <span className={`ld-setChip ${outstanding > 0.009 ? 'ld-setChip--due' : 'ld-setChip--ok'}`}>
                            <strong>{outstandingSets != null && outstanding > 0.009 ? setsLabel(outstandingSets) : money(outstanding)}</strong> outstanding
                            {outstandingSets != null && outstanding > 0.009 ? ` (${money(outstanding)})` : ''}
                        </span>
                    </div>

                    <div className="ld-list">
                        {status.deposits.map((d) => {
                            const remaining = Number(d.amount) - Number(d.refunded_total || 0);
                            const depositRefunds = refundsFor(d.id);
                            const dSets = asSets(d.amount);
                            return (
                                <div key={d.id} className="ld-item">
                                    <div className="ld-item-row">
                                        <span className="ld-item-main">
                                            <strong>{dSets != null ? setsLabel(dSets) : money(d.amount)}</strong>
                                            {dSets != null ? ` (${money(d.amount)})` : ''} · charged {formatDate(d.booking_date || d.charged_at)}
                                            {d.booking_id ? <> · booking <a href={`/bookings/${d.booking_id}`} className="ld-link">#{d.booking_id}</a></> : null}
                                            {' '}· <span className="ld-source">{d.source}</span>
                                        </span>
                                        {remaining > 0.009 && (
                                            <button
                                                type="button"
                                                className="ld-refund-btn"
                                                onClick={() => openRefundModal(d)}
                                            >
                                                Refund…
                                            </button>
                                        )}
                                        {/* A row with no live booking and no refunds is removable
                                            (a deleted booking's deposit that was never collected). */}
                                        {isManager && !d.booking_id && depositRefunds.length === 0 && (
                                            <button
                                                type="button"
                                                className="ld-refund-btn ld-remove-btn"
                                                onClick={async () => {
                                                    if (!window.confirm(`Remove this ${money(d.amount)} deposit row from the ledger?\n\nOnly do this for a duplicate — a deposit that was never actually collected. Refund history is untouched.`)) return;
                                                    try {
                                                        await axios.delete(`/api/linen-deposits/${d.id}`);
                                                        fetchStatus();
                                                    } catch (e) {
                                                        window.alert(e.response?.data?.error || 'Could not remove the row.');
                                                    }
                                                }}
                                            >
                                                Remove row
                                            </button>
                                        )}
                                    </div>
                                    {depositRefunds.length > 0 && (
                                        <div className="ld-refunds">
                                            {depositRefunds.map((r) => (
                                                <div key={r.id} className="ld-refund-line">
                                                    Refunded {asSets(r.amount) != null ? `${setsLabel(asSets(r.amount))} (${money(r.amount)})` : money(r.amount)} on {formatDate(r.refunded_at)}
                                                    {r.collected_booking_id ? <> · collected on <a className="ld-link" href={`/bookings/${r.collected_booking_id}`}>#{r.collected_booking_id}</a>{r.collected_booking_start ? ` (${formatDate(r.collected_booking_start)})` : ''}</> : null}
                                                    {r.invoice_reference ? <> · <strong>{r.invoice_reference}</strong></> : null}
                                                    {r.receipt_url ? <> · <a href={r.receipt_url} target="_blank" rel="noopener noreferrer" className="ld-link">receipt ↗</a></> : null}
                                                    {r.note ? <> — {r.note}</> : null}
                                                    {r.created_by_name ? <span className="ld-refund-by"> ({r.created_by_name})</span> : null}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {recordOpen && (
                <div className="ld-modal-overlay" onClick={() => !recordSubmitting && setRecordOpen(false)}>
                    <div className="ld-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="ld-modal-title">Record Linen Deposit</h3>
                        <p className="ld-modal-sub">
                            For deposits paid outside a tracked booking (bank transfer, cash, legacy).
                            Nothing is charged — this only records it so the automatic charge is skipped.
                        </p>

                        <div className="ld-modal-field">
                            <label>Linen sets (AED {SET_PRICE} each)</label>
                            <div className="ld-setPick">
                                {[1, 2, 3].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        className={`ld-setBtn ${!recordCustom && recordSets === n ? 'is-active' : ''}`}
                                        onClick={() => { setRecordCustom(false); setRecordSets(n); setAllowOverCap(false); }}
                                    >
                                        {setsLabel(n)}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className={`ld-setBtn ${recordCustom ? 'is-active' : ''}`}
                                    onClick={() => { setRecordCustom(true); setAllowOverCap(false); }}
                                >
                                    Other amount
                                </button>
                            </div>
                            {!recordCustom && (
                                <div className="ld-setTotal">= {money(recordSets * SET_PRICE)}</div>
                            )}
                        </div>

                        {recordCustom && (
                            <div className="ld-modal-field">
                                <label>Amount (AED)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    placeholder="legacy / odd amounts only"
                                    value={recordForm.amount}
                                    onChange={(e) => {
                                        setAllowOverCap(false);
                                        setRecordForm((p) => ({ ...p, amount: e.target.value }));
                                    }}
                                />
                            </div>
                        )}

                        <div className="ld-modal-field">
                            <label>Date paid</label>
                            <input
                                type="date"
                                value={recordForm.booking_date}
                                onChange={(e) => setRecordForm((p) => ({ ...p, booking_date: e.target.value }))}
                            />
                        </div>

                        <div className="ld-modal-field">
                            <label>Note (optional)</label>
                            <input
                                type="text"
                                placeholder="e.g. paid by bank transfer March 2025"
                                value={recordForm.note}
                                onChange={(e) => setRecordForm((p) => ({ ...p, note: e.target.value }))}
                            />
                        </div>

                        {recordError && <div className="ld-modal-error">{recordError}</div>}

                        {overCapBlocked && (
                            <div className="ld-modal-field ld-overcap">
                                <label className="ld-overcap-label">
                                    <input
                                        type="checkbox"
                                        checked={allowOverCap}
                                        onChange={(e) => setAllowOverCap(e.target.checked)}
                                    />
                                    <span>
                                        Record it anyway, above the cap. Only tick this if the linens really were
                                        re-charged (e.g. replaced after a loss) — it is not a way past a typo in
                                        the amount. Re-check the amount above first.
                                    </span>
                                </label>
                            </div>
                        )}

                        <div className="ld-modal-actions">
                            <button type="button" className="ld-btn-cancel" onClick={() => setRecordOpen(false)} disabled={recordSubmitting}>
                                Cancel
                            </button>
                            <button type="button" className="ld-btn-confirm" onClick={submitRecord} disabled={recordSubmitting}>
                                {recordSubmitting ? 'Saving…' : 'Record Deposit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {refundDeposit && (() => {
                const remaining = Number(refundDeposit.amount) - Number(refundDeposit.refunded_total || 0);
                const remSets = asSets(remaining);
                return (
                    <div className="ld-modal-overlay" onClick={closeRefundModal}>
                        <div className="ld-modal" onClick={(e) => e.stopPropagation()}>
                            <h3 className="ld-modal-title">Refund Linen Deposit</h3>
                            <p className="ld-modal-sub">
                                Deposit of {money(refundDeposit.amount)}
                                {refundDeposit.booking_id ? ` (booking #${refundDeposit.booking_id})` : ''} — remaining{' '}
                                <strong>{remSets != null ? `${setsLabel(remSets)} (${money(remaining)})` : money(remaining)}</strong>.
                            </p>

                            {/* HOW is it being returned? To the wallet (we move the
                                money now) or already returned outside the portal
                                (we only record it). */}
                            <div className="ld-modePick">
                                <button
                                    type="button"
                                    className={`ld-modeBtn ${refundMode === 'wallet' ? 'is-active' : ''}`}
                                    onClick={() => { setRefundMode('wallet'); setRefundError(''); }}
                                    disabled={!isManager}
                                    title={isManager ? '' : 'Managers only — this credits the client’s wallet'}
                                >
                                    <strong>Refund to wallet</strong>
                                    <span>Client can spend it or request a payout. VAT-free credit note recorded in Zoho.</span>
                                </button>
                                <button
                                    type="button"
                                    className={`ld-modeBtn ${refundMode === 'manual' ? 'is-active' : ''}`}
                                    onClick={() => { setRefundMode('manual'); setRefundError(''); }}
                                >
                                    <strong>Already refunded</strong>
                                    <span>Handled outside the portal (bank/cash) — record it with the date and proof.</span>
                                </button>
                            </div>

                            {/* WHEN did we take the linens back — the final-return
                                collection booking, on the refund's record. */}
                            <div className="ld-modal-field">
                                <label>Collected on booking (final return)</label>
                                <Select
                                    classNamePrefix="ld-select"
                                    options={collectedOptions}
                                    value={collectedBooking}
                                    onChange={setCollectedBooking}
                                    isClearable
                                    placeholder="Select the booking the linens were collected on…"
                                    noOptionsMessage={() => 'No bookings for this property'}
                                    /* The modal scrolls (capped height), and an
                                       in-modal menu gets clipped by its
                                       overflow — portal the menu to the body. */
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                                />
                            </div>

                            {refundMode === 'wallet' ? (
                                <>
                                    {remSets ? (
                                        <div className="ld-modal-field">
                                            <label>Sets to refund</label>
                                            <div className="ld-setPick">
                                                {Array.from({ length: remSets }, (_, i) => i + 1).map((n) => (
                                                    <button
                                                        key={n}
                                                        type="button"
                                                        className={`ld-setBtn ${refundSets === n ? 'is-active' : ''}`}
                                                        onClick={() => setRefundSets(n)}
                                                    >
                                                        {setsLabel(n)}
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="ld-setTotal">= {money(refundSets * SET_PRICE)} to the wallet</div>
                                        </div>
                                    ) : (
                                        <div className="ld-modal-field">
                                            <label>Amount (AED)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0.01"
                                                value={refundForm.amount}
                                                onChange={(e) => setRefundForm((p) => ({ ...p, amount: e.target.value }))}
                                            />
                                        </div>
                                    )}
                                    <div className="ld-modal-field">
                                        <label>Note (optional — appears on the credit note)</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. client stopped linen hire, sets returned in good condition"
                                            value={refundForm.note}
                                            onChange={(e) => setRefundForm((p) => ({ ...p, note: e.target.value }))}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="ld-modal-field">
                                        <label>Amount (AED)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            value={refundForm.amount}
                                            onChange={(e) => setRefundForm((p) => ({ ...p, amount: e.target.value }))}
                                        />
                                    </div>
                                    <div className="ld-modal-field">
                                        <label>Date refunded</label>
                                        <input
                                            type="date"
                                            value={refundForm.refunded_at}
                                            onChange={(e) => setRefundForm((p) => ({ ...p, refunded_at: e.target.value }))}
                                        />
                                    </div>
                                    <div className="ld-modal-field">
                                        <label>Note (optional)</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. linens returned in good condition"
                                            value={refundForm.note}
                                            onChange={(e) => setRefundForm((p) => ({ ...p, note: e.target.value }))}
                                        />
                                    </div>

                                    <div className="ld-proof">
                                        <div className="ld-proof-title">Proof of refund (required — one of the two)</div>
                                        <div className="ld-modal-field">
                                            <label>Zoho invoice</label>
                                            <Select
                                                options={invoiceOptions}
                                                isLoading={invoicesLoading}
                                                isClearable
                                                isSearchable
                                                placeholder="Select the client's invoice (e.g. INV-007173)…"
                                                value={invoiceOptions.find((o) => o.value === refundForm.invoice_reference) || null}
                                                onChange={(opt) => setRefundForm((p) => ({ ...p, invoice_reference: opt?.value || '' }))}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 100000 }) }}
                                                noOptionsMessage={() => invoicesLoading ? 'Loading…' : 'No invoices found for this client'}
                                            />
                                        </div>
                                        <div className="ld-modal-field">
                                            <label>Or transfer receipt (image / PDF)</label>
                                            <input
                                                type="file"
                                                accept="image/*,application/pdf"
                                                onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                                            />
                                            {receiptFile && (
                                                <div className="ld-proof-file">
                                                    {receiptFile.name}{' '}
                                                    <button type="button" className="ld-proof-clear" onClick={() => setReceiptFile(null)}>✕</button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            {refundError && <div className="ld-modal-error">{refundError}</div>}
                            {refundOverCap && refundMode === 'wallet' && (
                                <label className="ld-overcap-ack">
                                    <input type="checkbox" checked={refundOverCapAck} onChange={(e) => setRefundOverCapAck(e.target.checked)} />
                                    {' '}I have checked this property's deposit rows — the client really paid this, refund anyway.
                                </label>
                            )}

                            <div className="ld-modal-actions">
                                <button type="button" className="ld-btn-cancel" onClick={closeRefundModal} disabled={refundSubmitting}>
                                    Cancel
                                </button>
                                <button type="button" className="ld-btn-confirm" onClick={submitRefund} disabled={refundSubmitting}>
                                    {refundSubmitting
                                        ? 'Saving…'
                                        : refundMode === 'wallet' ? 'Refund to wallet' : 'Record Refund'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

export default LinenDepositSection;
